/**
 * UnitsListPage
 * Gestão de Unidades (posto de trabalho) por empresa.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, Chip, Dialog, Checkbox, FormControlLabel, FormHelperText,
  DialogTitle, DialogContent, DialogActions, Menu, MenuItem, Tooltip,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, PlaylistAdd as AplicarIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { pode } from '../../authorization/pode';
import { useAcao, useVeredito } from '../../authorization/useVeredito';
import { PageShell, DataTable, type DataTableColumn } from '../../components';
import { ActionButton, InlineFilterBar, InlineSelect } from '../../components/common';
import type { ActiveChip } from '../../hooks/useFilterState';
import { companyService } from '../../services/companyService';
import { unitService } from '../../services/unitService';
import { montarEndereco } from '../../components/applications/enderecoDoAssistente';
import {
  AlcanceDaUnidade, Unit, UnitNaLista, UnitStatus, UNIT_STATUS_LABELS,
} from '../../types/unit';

interface CompanyOption { id: number; name: string; }

const STATUS_OPTIONS = Object.values(UnitStatus).map((status) => ({
  value: status,
  label: UNIT_STATUS_LABELS[status],
}));

/**
 * Aplicar à unidade leva à tela de aplicação já com a unidade escolhida (#541). Cada item pede a
 * escrita da aplicação dele, na perspectiva que a tela de destino exige.
 */
const APLICAR_NA_UNIDADE = [
  { label: 'Aplicar riscos', path: '/risk-applications', permissao: 'risk_application.create' },
  { label: 'Aplicar exames', path: '/exam-applications', permissao: 'exam_application.create' },
  { label: 'Aplicar fontes', path: '/source-applications', permissao: 'source_application.create' },
] as const;

const destinoDaAplicacao = (path: string, unit: Unit, voltar: string): string =>
  montarEndereco(path, { nivel: 'unit', ids: { company: unit.companyId, unit: unit.id }, voltar });

type ContagemDeAplicacoes = NonNullable<AlcanceDaUnidade['aplicacoes']>;

/**
 * O que some junto com a unidade, no gênero e no número do que foi contado. Só entra o que o
 * perfil pode ler: contagem ausente não é zero, é algo que esta pessoa não enxerga.
 */
function AplicacoesQueSomem({ aplicacoes }: { aplicacoes?: ContagemDeAplicacoes }) {
  const itens = [
    { n: aplicacoes?.riscos ?? 0, um: 'risco', varios: 'riscos', feminino: false },
    { n: aplicacoes?.exames ?? 0, um: 'exame', varios: 'exames', feminino: false },
    { n: aplicacoes?.fontes ?? 0, um: 'fonte', varios: 'fontes', feminino: true },
  ].filter((item) => item.n > 0);
  if (itens.length === 0) return null;

  const total = itens.reduce((soma, item) => soma + item.n, 0);
  const feminino = itens.every((item) => item.feminino);
  const fecho =
    total === 1
      ? feminino ? 'aplicada. Ela será removida' : 'aplicado. Ele será removido'
      : feminino ? 'aplicadas. Elas serão removidas' : 'aplicados. Eles serão removidos';

  return (
    <Typography sx={{ mt: 1.5 }}>
      Esta unidade tem{' '}
      {itens.map((item, indice) => (
        <React.Fragment key={item.um}>
          {indice > 0 && (indice === itens.length - 1 ? ' e ' : ', ')}
          <b>{`${item.n} ${item.n === 1 ? item.um : item.varios}`}</b>
        </React.Fragment>
      ))}{' '}
      {fecho} junto com ela.
    </Typography>
  );
}

const columns: DataTableColumn<UnitNaLista>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (unit) => (
      <Box>
        <Typography variant="body2">{unit.name}</Typography>
        {unit.isObra && <Typography variant="body2" color="text.secondary">Obra</Typography>}
      </Box>
    ),
  },
  { id: 'location', label: 'Localização', render: (unit) => unit.location || '—' },
  {
    id: 'setores',
    label: 'Setores',
    render: (unit) =>
      unit.setoresLigados > 0 ? (
        unit.setoresLigados
      ) : (
        <Tooltip title="Sem setor ligado, o que for aplicado à unidade não alcança ninguém.">
          <Typography component="span" variant="body2" color="text.secondary">Nenhum</Typography>
        </Tooltip>
      ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (unit) => (
      <Chip
        size="small"
        label={UNIT_STATUS_LABELS[unit.status]}
        color={unit.status === UnitStatus.ACTIVE ? 'success' : 'default'}
        variant="outlined"
      />
    ),
  },
];

interface UnitFormState {
  name: string;
  location: string;
  status: UnitStatus;
  isObra: boolean;
}

const emptyForm: UnitFormState = { name: '', location: '', status: UnitStatus.ACTIVE, isObra: false };

const OBRA_COM_FRENTES =
  'Esta obra tem frentes. Encerre e exclua as frentes em Frentes de obra para desmarcar.';
const OBRA_AJUDA =
  'Obras ganham frentes, com etapas, riscos e trabalhadores, em Segurança do trabalho. Desmarcar só é possível sem frentes cadastradas.';

const UnitsListPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const editar = useAcao('unit.update', 'Editar');
  const excluir = useAcao('unit.delete', 'Excluir');
  const navigate = useNavigate();
  const location = useLocation();
  // "Concluir", na aplicação, volta para cá.
  const aqui = `${location.pathname}${location.search}`;
  const aplicarRiscos = useVeredito(
    APLICAR_NA_UNIDADE[0].path,
    pode(APLICAR_NA_UNIDADE[0].permissao, { perspectiva: 'plataforma' })
  );
  const aplicarExames = useVeredito(
    APLICAR_NA_UNIDADE[1].path,
    pode(APLICAR_NA_UNIDADE[1].permissao, { perspectiva: 'plataforma' })
  );
  const aplicarFontes = useVeredito(
    APLICAR_NA_UNIDADE[2].path,
    pode(APLICAR_NA_UNIDADE[2].permissao, { perspectiva: 'plataforma' })
  );
  const aplicacoes = [
    { ...APLICAR_NA_UNIDADE[0], veredito: aplicarRiscos },
    { ...APLICAR_NA_UNIDADE[1], veredito: aplicarExames },
    { ...APLICAR_NA_UNIDADE[2], veredito: aplicarFontes },
  ];
  // Aplicar fica à vista mesmo sem escrita nenhuma, desabilitado com o motivo do primeiro item.
  const algumaAplicacaoLiberada = aplicacoes.some(({ veredito }) => veredito.liberado);
  const aplicar = algumaAplicacaoLiberada
    ? { disabled: false, tooltip: 'Aplicar' }
    : { disabled: true, tooltip: `Aplicar — ${aplicarRiscos.explicacao ?? 'sem acesso'}` };

  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UnitStatus | ''>('');
  const [units, setUnits] = useState<UnitNaLista[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UnitNaLista | null>(null);
  const [form, setForm] = useState<UnitFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Unit | null>(null);
  const [alcanceDaExclusao, setAlcanceDaExclusao] = useState<AlcanceDaUnidade | null>(null);
  const [menuAplicar, setMenuAplicar] = useState<{ anchor: HTMLElement; unit: Unit } | null>(null);

  // A ação da tabela entrega só a linha: o botão clicado, que ancora o menu, vem do clique.
  const botaoClicado = useRef<HTMLElement | null>(null);

  useEffect(() => {
    companyService.getCompaniesForDropdown()
      .then((list) => {
        setCompanies(list);
        if (list.length === 1) setSelectedCompany(list[0] ?? null);
      })
      .catch(() => enqueueSnackbar('Erro ao carregar empresas', { variant: 'error' }));
  }, [enqueueSnackbar]);

  const loadUnits = useCallback(async (companyId: number, filters: { search: string; status: UnitStatus | '' }) => {
    setLoading(true);
    try {
      // Lista de gestão: todos os status (ativas e inativas) por padrão, diferente do select do Setor
      const result = await unitService.getUnits({
        companyId,
        limit: 100,
        search: filters.search.trim() || undefined,
        filters: { status: filters.status || undefined },
      });
      setUnits(result.data);
      setTotal(result.pagination.total);
    } catch {
      enqueueSnackbar('Erro ao carregar unidades', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (selectedCompany) {
      loadUnits(selectedCompany.id, { search, status });
    } else {
      setUnits([]);
      setTotal(0);
    }
  }, [selectedCompany, search, status, loadUnits]);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
  };

  const chips: ActiveChip[] = [];
  if (status) {
    chips.push({
      key: 'status',
      label: 'Status',
      value: UNIT_STATUS_LABELS[status],
      onDelete: () => setStatus(''),
    });
  }

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (unit: UnitNaLista) => {
    setEditing(unit);
    setForm({ name: unit.name, location: unit.location ?? '', status: unit.status, isObra: Boolean(unit.isObra) });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedCompany) return;
    if (!form.name.trim()) {
      enqueueSnackbar('Nome da unidade é obrigatório', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await unitService.updateUnit(editing.id, {
          name: form.name.trim(),
          location: form.location.trim() || undefined,
          status: form.status,
          isObra: form.isObra,
        });
        enqueueSnackbar('Unidade atualizada', { variant: 'success' });
      } else {
        await unitService.createUnit({
          companyId: selectedCompany.id,
          name: form.name.trim(),
          location: form.location.trim() || undefined,
          status: form.status,
          isObra: form.isObra,
        });
        enqueueSnackbar('Unidade criada', { variant: 'success' });
      }
      setDialogOpen(false);
      await loadUnits(selectedCompany.id, { search, status });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao salvar unidade', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const pedirExclusao = (unit: Unit) => {
    setDeleteTarget(unit);
    setAlcanceDaExclusao(null);
    unitService.getAlcance(unit.id)
      .then(setAlcanceDaExclusao)
      .catch(() => setAlcanceDaExclusao(null));
  };

  const abrirMenuAplicar = (unit: Unit) => {
    if (botaoClicado.current) setMenuAplicar({ anchor: botaoClicado.current, unit });
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !selectedCompany) return;
    try {
      await unitService.deleteUnit(deleteTarget.id);
      enqueueSnackbar('Unidade excluída', { variant: 'success' });
      await loadUnits(selectedCompany.id, { search, status });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao excluir unidade', { variant: 'error' });
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Unidades' }]}
      title="Unidades"
      subtitle="Postos de trabalho de uma empresa (mesmo CNPJ)"
      actions={
        <ActionButton
          acao="unit.create"
          rotulo="Nova Unidade"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={!selectedCompany}
          onClick={openCreate}
        >
          Nova Unidade
        </ActionButton>
      }
    >
      <Box sx={{ mb: 2 }}>
        <InlineFilterBar
          searchPlaceholder="Buscar por endereço/referência..."
          searchValue={search}
          onSearchChange={setSearch}
          resultCount={selectedCompany ? total : undefined}
          resultNoun="unidades"
          chips={chips}
          onClearAll={clearFilters}
          loading={loading}
        >
          <InlineSelect
            label="Empresa"
            value={selectedCompany ? String(selectedCompany.id) : ''}
            options={companies.map((c) => ({ value: String(c.id), label: c.name }))}
            onChange={(v) => setSelectedCompany(companies.find((c) => String(c.id) === v) ?? null)}
            emptyLabel="Selecione a empresa"
            minWidth={200}
            maxWidth={320}
          />
          <InlineSelect
            label="Status"
            value={status}
            options={STATUS_OPTIONS}
            onChange={(v) => setStatus(v as UnitStatus | '')}
            emptyLabel="Todos os status"
            disabled={!selectedCompany}
            minWidth={160}
          />
        </InlineFilterBar>
      </Box>

      <Box
        onClickCapture={(event) => {
          botaoClicado.current = event.target instanceof Element ? event.target.closest('button') : null;
        }}
      >
      <DataTable
        columns={columns}
        rows={units}
        getRowId={(unit) => unit.id}
        loading={loading}
        emptyState={
          selectedCompany
            ? {
                title: 'Nenhuma unidade cadastrada ainda',
                description:
                  'Unidades são os endereços onde a empresa opera, usados para localizar funcionários e exames.',
                prereqChain: 'units',
                action: {
                  label: 'Cadastrar unidade',
                  node: 'units',
                  deniedHint: 'Peça ao administrador da empresa para cadastrar uma unidade.',
                },
              }
            : {
                title: 'Selecione uma empresa para ver as unidades',
                description:
                  'As unidades são os endereços onde a empresa opera; escolha uma empresa no filtro acima.',
              }
        }
        filtersActive={search.trim() !== '' || status !== ''}
        onClearFilters={clearFilters}
        actions={[
          {
            icon: <AplicarIcon fontSize="small" />,
            onClick: abrirMenuAplicar,
            ...aplicar,
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: openEdit,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: pedirExclusao,
            ...excluir,
          },
        ]}
      />
      </Box>

      <Menu anchorEl={menuAplicar?.anchor} open={menuAplicar != null} onClose={() => setMenuAplicar(null)}>
        {aplicacoes.map(({ label, path, veredito }) => (
          <Tooltip key={path} title={veredito.explicacao ?? ''} placement="left">
            <span>
              <MenuItem
                disabled={!veredito.liberado}
                onClick={() => {
                  if (!menuAplicar) return;
                  setMenuAplicar(null);
                  navigate(destinoDaAplicacao(path, menuAplicar.unit, aqui));
                }}
              >
                {label}
              </MenuItem>
            </span>
          </Tooltip>
        ))}
      </Menu>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editar Unidade' : 'Nova Unidade'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Nome" fullWidth required size="small" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label="Localização" fullWidth size="small" value={form.location}
              placeholder="Endereço / referência do posto"
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            />
            <TextField
              label="Status" select fullWidth size="small" value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as UnitStatus }))}
            >
              {Object.values(UnitStatus).map((s) => (
                <MenuItem key={s} value={s}>{UNIT_STATUS_LABELS[s]}</MenuItem>
              ))}
            </TextField>
            <Box>
              <FormControlLabel
                label="Esta unidade é uma obra"
                control={
                  <Checkbox
                    checked={form.isObra}
                    disabled={Boolean(editing?.hasFronts)}
                    onChange={(e) => setForm((f) => ({ ...f, isObra: e.target.checked }))}
                    inputProps={{ 'aria-describedby': 'unidade-obra-ajuda' }}
                  />
                }
              />
              <FormHelperText id="unidade-obra-ajuda" sx={{ mt: 0, ml: 4 }}>
                {editing?.hasFronts ? OBRA_COM_FRENTES : OBRA_AJUDA}
              </FormHelperText>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteTarget != null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Excluir unidade</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir a unidade <b>{deleteTarget?.name}</b>?
          </Typography>
          <AplicacoesQueSomem aplicacoes={alcanceDaExclusao?.aplicacoes} />
          <Typography color="text.secondary" sx={{ mt: 1.5 }}>
            Os setores e funcionários ligados a ela ficam sem unidade.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Excluir</Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default UnitsListPage;
